/**
 * The API client.
 *
 * One `request` helper, a typed wrapper per endpoint, and errors that carry the
 * server's code so a caller can branch on it. Every shape comes from
 * worker/wire.ts through `@wire` — type-only, erased before the bundler runs.
 *
 * `streamTurn` reads the turn endpoints' server-sent events: a
 * `TextDecoderStream` over `fetch`, the same forty lines website-main and
 * Scope use, rather than EventSource (which cannot POST) or a dependency.
 */

import type {
  ApiErrorBody,
  Artifact,
  ArtifactLibrary,
  ArtifactList,
  ArtifactShare,
  ArtifactVersion,
  DesignSystem,
  DesignSystemCreate,
  DesignSystemList,
  DesignSystemPatch,
  SharedArtifact,
  ShareRequest,
  RemixResult,
  ApiKey,
  ApiKeyList,
  ApiKeyPatch,
  CreateApiKeyRequest,
  CreatedApiKey,
  BranchRequest,
  CardAnswer,
  Chat,
  ChatCreate,
  ChatList,
  ChatPatch,
  ChatSummary,
  Connector,
  ConnectorAuthorize,
  ConnectorCreate,
  ConnectorList,
  ConnectorPatch,
  ContinueRequest,
  Device,
  DeviceList,
  DevicePut,
  DeviceReport,
  ErrorCode,
  FileInfo,
  GalleryEvent,
  GalleryList,
  GalleryRequest,
  GalleryThread,
  VideoJob,
  VideoJobList,
  VideoRequest,
  LeafRequest,
  Me,
  MemoryDoc,
  MemoryEditRequest,
  MemoryEditResult,
  MemoryImportEvent,
  MemoryImportRequest,
  MemoryList,
  Message,
  ModelCatalogue,
  OnboardingFinish,
  OnboardingState,
  PairingAnswer,
  Project,
  ProjectCreate,
  ProjectList,
  ProjectPatch,
  ProviderKey,
  ProviderList,
  ProviderPut,
  ProviderStatus,
  RetryRequest,
  Settings,
  SettingsPatch,
  Skill,
  SkillCreate,
  SkillList,
  SkillPatch,
  Automation,
  AutomationCreate,
  AutomationList,
  AutomationPatch,
  AutomationRuns,
  AutomationRunStarted,
  Transcript,
  TurnEvent,
  TurnRequest,
  ReplyDebug,
  ResearchStatus,
  UsageHistory,
  UsageReport,
  VoiceName
} from '@wire';
import { DEFAULT_EFFORT, DEFAULT_GALLERY_MODEL, DEFAULT_MODEL, DEFAULT_VOICE } from '@wire';

export type * from '@wire';

const BASE = '/api/assist';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network' | 'bad_origin' | 'method_not_allowed',
    message: string,
    readonly details: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function errorFrom(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
  const { code, message, ...details } = body?.error ?? {
    code: 'internal' as const,
    message: 'Something went wrong.'
  };
  return new ApiError(res.status, code, message, details);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      // The session cookie is domain-wide; same-origin is all this needs.
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}),
        ...init.headers
      }
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach Assist.');
  }

  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string) => request<T>(path);
const send = <T>(method: string, path: string, body?: unknown) =>
  request<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ── identity and settings ───────────────────────────────────────────────────

/** What a field of `Settings` is when the Worker's answer leaves it out: a
 *  Worker older or newer than this page may, and a page that read one
 *  unguarded threw until it was reloaded. */
const SETTINGS_DEFAULTS: Settings = {
  defaultModel: DEFAULT_MODEL,
  defaultEffort: DEFAULT_EFFORT,
  warnThirdParty: true,
  voice: DEFAULT_VOICE,
  developerMode: false,
  timeZone: null,
  imageModel: DEFAULT_GALLERY_MODEL
};

export const withSettingsDefaults = (s: Partial<Settings> | null | undefined): Settings => ({
  ...SETTINGS_DEFAULTS,
  ...Object.fromEntries(Object.entries(s ?? {}).filter(([, v]) => v !== undefined))
});

const meWithDefaults = (me: Me): Me => ({ ...me, settings: withSettingsDefaults(me.settings) });

export const getMe = () => get<Me>('/me').then(meWithDefaults);
export const getSettings = () => get<Settings>('/settings').then(withSettingsDefaults);
export const patchSettings = (p: SettingsPatch) => send<Settings>('PATCH', '/settings', p).then(withSettingsDefaults);

// ── models and providers ────────────────────────────────────────────────────

export const getModels = () => get<ModelCatalogue>('/models');
export const listProviders = () => get<ProviderList>('/providers');
export const putProvider = (provider: ProviderKey, body: ProviderPut) =>
  send<ProviderStatus>('PUT', `/providers/${provider}`, body);
export const deleteProvider = (provider: ProviderKey) =>
  send<void>('DELETE', `/providers/${provider}`);

// ── chats ───────────────────────────────────────────────────────────────────

export const listChats = () => get<ChatList>('/chats');
export const createChat = (body: ChatCreate) => send<Chat>('POST', '/chats', body);
export const getChat = (id: string) => get<Chat>(`/chats/${encodeURIComponent(id)}`);
export const patchChat = (id: string, body: ChatPatch) =>
  send<ChatSummary>('PATCH', `/chats/${encodeURIComponent(id)}`, body);
export const deleteChat = (id: string) => send<void>('DELETE', `/chats/${encodeURIComponent(id)}`);
export const branchChat = (id: string, body: BranchRequest) =>
  send<Chat>('POST', `/chats/${encodeURIComponent(id)}/branch`, body);
export const researchStatus = (chatId: string, messageId: string) =>
  get<ResearchStatus>(`/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/research`);
// ── dispatch ────────────────────────────────────────────────────────────────

export const listDevices = () => get<DeviceList>('/devices');
/** The desktop app's own: what the PC is and what it runs. */
export const putDevice = (id: string, body: DevicePut) => send<Device>('PUT', `/devices/${encodeURIComponent(id)}`, body);
/** Forget a PC, and its thread with it. */
export const forgetDevice = (id: string) => send<void>('DELETE', `/devices/${encodeURIComponent(id)}`);
/** A PC's thread, made the first time it is opened. */
export const openDeviceChat = (id: string) => send<Chat>('POST', `/devices/${encodeURIComponent(id)}/chat`);
/** The desktop app's own: what it is doing with a reply waiting on it. */
export const reportDevice = (id: string, body: DeviceReport) => send<void>('POST', `/devices/${encodeURIComponent(id)}/report`, body);
/** The desktop app's own: what it made of a phone's pairing. */
export const answerPairing = (id: string, phone: string, body: PairingAnswer) =>
  send<void>('PUT', `/devices/${encodeURIComponent(id)}/pairings/${encodeURIComponent(phone)}`, body);

// ── connectors ──────────────────────────────────────────────────────────────

export const listConnectors = () => get<ConnectorList>('/connectors');
export const addConnector = (body: ConnectorCreate) => send<Connector>('POST', '/connectors', body);
export const patchConnector = (id: string, body: ConnectorPatch) =>
  send<Connector>('PATCH', `/connectors/${encodeURIComponent(id)}`, body);
export const deleteConnector = (id: string) => send<void>('DELETE', `/connectors/${encodeURIComponent(id)}`);
export const refreshConnector = (id: string) => send<Connector>('POST', `/connectors/${encodeURIComponent(id)}/refresh`);
export const authorizeConnector = (id: string) =>
  send<ConnectorAuthorize>('POST', `/connectors/${encodeURIComponent(id)}/authorize`);

export const setLeaf = (id: string, body: LeafRequest) =>
  send<Chat>('POST', `/chats/${encodeURIComponent(id)}/leaf`, body);
export const answerCard = (chatId: string, messageId: string, body: CardAnswer) =>
  send<Message>(
    'POST',
    `/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/answer`,
    body
  );

// ── skills ──────────────────────────────────────────────────────────────────

export const listSkills = () => get<SkillList>('/skills');
export const createSkill = (body: SkillCreate) => send<Skill>('POST', '/skills', body);
export const patchSkill = (id: string, body: SkillPatch) => send<Skill>('PATCH', `/skills/${encodeURIComponent(id)}`, body);
export const deleteSkill = (id: string) => send<void>('DELETE', `/skills/${encodeURIComponent(id)}`);

// ── automations ─────────────────────────────────────────────────────────────

export const listAutomations = () => get<AutomationList>('/automations');
export const createAutomation = (body: AutomationCreate) => send<Automation>('POST', '/automations', body);
export const patchAutomation = (id: string, body: AutomationPatch) =>
  send<Automation>('PATCH', `/automations/${encodeURIComponent(id)}`, body);
export const deleteAutomation = (id: string) => send<void>('DELETE', `/automations/${encodeURIComponent(id)}`);
export const runAutomation = (id: string) => send<AutomationRunStarted>('POST', `/automations/${encodeURIComponent(id)}/run`);
export const listRuns = (id: string) => get<AutomationRuns>(`/automations/${encodeURIComponent(id)}/runs`);

// ── API keys ────────────────────────────────────────────────────────────────

export const listApiKeys = () => get<ApiKeyList>('/keys');
export const createApiKey = (body: CreateApiKeyRequest) => send<CreatedApiKey>('POST', '/keys', body);
export const patchApiKey = (id: string, body: ApiKeyPatch) => send<ApiKey>('PATCH', `/keys/${encodeURIComponent(id)}`, body);
export const deleteApiKey = (id: string) => send<void>('DELETE', `/keys/${encodeURIComponent(id)}`);

// ── artifacts and design systems ────────────────────────────────────────────

const art = (id: string) => `/artifacts/${encodeURIComponent(id)}`;
export const listArtifacts = (chatId: string) => get<ArtifactList>(`/artifacts?chat=${encodeURIComponent(chatId)}`);
export const getArtifact = (id: string) => get<Artifact>(art(id));
export const getArtifactVersion = (id: string, version: number) => get<ArtifactVersion>(`${art(id)}/versions/${version}`);
export const shareArtifact = (id: string, body: ShareRequest) => send<ArtifactShare>('PUT', `${art(id)}/share`, body);
export const getShared = (token: string) => get<SharedArtifact>(`/shared/${encodeURIComponent(token)}`);
export const reportShared = (token: string, reason: string) =>
  send<void>('POST', `/shared/${encodeURIComponent(token)}/report`, { reason });
export const remixShared = (token: string) => send<RemixResult>('POST', `/shared/${encodeURIComponent(token)}/remix`);
export const listLibrary = (before: string | null) =>
  get<ArtifactLibrary>(`/artifacts${before ? `?before=${encodeURIComponent(before)}` : ''}`);

export const listDesignSystems = () => get<DesignSystemList>('/design-systems');
export const createDesignSystem = (body: DesignSystemCreate) => send<DesignSystem>('POST', '/design-systems', body);
export const patchDesignSystem = (id: string, body: DesignSystemPatch) =>
  send<DesignSystem>('PATCH', `/design-systems/${encodeURIComponent(id)}`, body);
export const deleteDesignSystem = (id: string) => send<void>('DELETE', `/design-systems/${encodeURIComponent(id)}`);
export const designSystemFromArtifact = (artifactId: string) =>
  send<DesignSystem>('POST', '/design-systems/from-artifact', { artifactId });

// ── projects ────────────────────────────────────────────────────────────────

export const listProjects = () => get<ProjectList>('/projects');
export const createProject = (body: ProjectCreate) => send<Project>('POST', '/projects', body);
export const getProject = (id: string) => get<Project>(`/projects/${encodeURIComponent(id)}`);
export const patchProject = (id: string, body: ProjectPatch) =>
  send<Project>('PATCH', `/projects/${encodeURIComponent(id)}`, body);
export const deleteProject = (id: string) =>
  send<void>('DELETE', `/projects/${encodeURIComponent(id)}`);
export const deleteProjectMemory = (id: string, itemId: string) =>
  send<void>('DELETE', `/projects/${encodeURIComponent(id)}/memory/${encodeURIComponent(itemId)}`);

// ── memory ──────────────────────────────────────────────────────────────────

export const listMemory = () => get<MemoryList>('/memory');
export const getMemoryDoc = (id: string) => get<MemoryDoc>(`/memory/${encodeURIComponent(id)}`);
export const deleteMemoryDoc = (id: string) =>
  send<void>('DELETE', `/memory/${encodeURIComponent(id)}`);
export const editMemory = (body: MemoryEditRequest) =>
  send<MemoryEditResult>('POST', '/memory/edit', body);

/**
 * Import another assistant's memory, calling `onEvent` as each part lands.
 * Rejects when the import is refused before it starts, as `streamTurn` does;
 * abort to stop it after the part in hand.
 */
export async function streamMemoryImport(
  text: string,
  onEvent: (e: MemoryImportEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const body: MemoryImportRequest = { text };
  let res: Response;
  try {
    res = await fetch(`${BASE}/memory/import`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {})
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    throw new ApiError(0, 'network', 'Could not reach Assist.');
  }
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res as Response & { body: NonNullable<Response['body']> }, onEvent);
}

// ── files ───────────────────────────────────────────────────────────────────

export function uploadFile(
  file: File,
  parent: { projectId?: string; chatId?: string } = {}
): Promise<FileInfo> {
  const form = new FormData();
  form.set('file', file);
  if (parent.projectId) form.set('projectId', parent.projectId);
  if (parent.chatId) form.set('chatId', parent.chatId);
  return request<FileInfo>('/files', { method: 'POST', body: form });
}

/** Where a file's bytes are served, for `<img src>` and download links. */
export const fileUrl = (id: string): string => `${BASE}/files/${encodeURIComponent(id)}`;
export const deleteFile = (id: string) => send<void>('DELETE', `/files/${encodeURIComponent(id)}`);

// ── usage, onboarding, dictation ────────────────────────────────────────────

export const getUsage = () => get<UsageReport>('/usage');
/** A Mauvely developer's usage by day and by kind. */
export const getUsageHistory = (days: 30 | 90) => get<UsageHistory>(`/usage/history?days=${days}`);
/** A reply's Details, for a Mauvely developer. */
export const getReplyDebug = (chatId: string, messageId: string) =>
  get<ReplyDebug>(`/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/debug`);
export const getOnboarding = () => get<OnboardingState>('/onboarding');
export const finishOnboarding = (body: OnboardingFinish) =>
  send<Me>('POST', '/onboarding/finish', body).then(meWithDefaults);

export async function transcribe(audio: Blob): Promise<Transcript> {
  return request<Transcript>('/transcribe', {
    method: 'POST',
    body: audio,
    headers: { 'content-type': audio.type || 'audio/webm' }
  });
}

/** A piece of a reply, said aloud, as WAV. `voice` tries out another than the
 *  setting's. */
export async function speak(text: string, signal?: AbortSignal, voice?: VoiceName): Promise<ArrayBuffer> {
  const res = await fetch(`${BASE}/speak`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(voice ? { text, voice } : { text }),
    ...(signal ? { signal } : {})
  });
  if (!res.ok) throw await errorFrom(res);
  return res.arrayBuffer();
}

// ── the turn stream ─────────────────────────────────────────────────────────

/**
 * POST a turn and call `onEvent` for each server-sent event until the stream
 * ends. Resolves when it closes; rejects with an `ApiError` when the request
 * is refused before streaming starts (429 `limit_reached`, 403 `ai_disabled`,
 * …). An error *during* the stream arrives as an `error` event instead.
 *
 * Aborting with `signal` stops reading, not the reply, which goes on and is
 * saved whole: `stopReply` stops it.
 *
 * Resolves `'queued'` for a message in a PC's thread that the Worker held,
 * because the last reply there waits on the PC (202): no stream follows.
 */
export async function streamTurn(
  path: `/chats/${string}/turn` | `/chats/${string}/retry` | `/chats/${string}/continue` | '/onboarding/turn',
  body: TurnRequest | RetryRequest | ContinueRequest,
  onEvent: (e: TurnEvent) => void,
  signal?: AbortSignal
): Promise<'queued' | void> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {})
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    throw new ApiError(0, 'network', 'Could not reach Assist.');
  }
  if (res.status === 202) {
    void res.body?.cancel().catch(() => undefined);
    return 'queued';
  }
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res as Response & { body: NonNullable<Response['body']> }, onEvent);
}

/**
 * Join the reply running in a chat: `onEvent` for each event from `attach`
 * until the stream ends. False when no reply is running.
 */
export async function followTurn(chatId: string, onEvent: (e: TurnEvent) => void, signal?: AbortSignal): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/chats/${encodeURIComponent(chatId)}/live`, {
      credentials: 'same-origin',
      headers: { accept: 'text/event-stream' },
      ...(signal ? { signal } : {})
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return true;
    throw new ApiError(0, 'network', 'Could not reach Assist.');
  }
  if (res.status === 204) return false;
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res as Response & { body: NonNullable<Response['body']> }, onEvent);
  return true;
}

/** Stop the reply running in a chat. Resolves once it has stopped. */
export const stopReply = (chatId: string) => send<void>('POST', `/chats/${encodeURIComponent(chatId)}/stop`);

/** Server-sent events off a body, one parsed event at a time, until it ends
 *  or is aborted. */
async function readSse<E>(res: Response & { body: NonNullable<Response['body']> }, onEvent: (e: E) => void): Promise<void> {
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;

      // Events are separated by a blank line; the last piece may be partial.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const data = frame
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).trimStart())
          .join('\n');
        if (!data) continue;
        try {
          onEvent(JSON.parse(data) as E);
        } catch {
          // A malformed frame is skipped rather than ending the reply.
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    throw err;
  }
}

// ── gallery ─────────────────────────────────────────────────────────────────

export const listGallery = (before?: string | null) =>
  get<GalleryList>(before ? `/gallery?before=${encodeURIComponent(before)}` : '/gallery');

/** Where a Gallery image is served; `download` to save it rather than show it. */
export const galleryImageUrl = (id: string, download = false): string =>
  `${BASE}/gallery/${encodeURIComponent(id)}/image${download ? '?download=1' : ''}`;

export const deleteGalleryImage = (id: string) => send<void>('DELETE', `/gallery/${encodeURIComponent(id)}`);

/** Where a Gallery video plays from, by the range; `download` to save it. */
export const galleryVideoUrl = (id: string, download = false): string =>
  `${BASE}/gallery/${encodeURIComponent(id)}/video${download ? '?download=1' : ''}`;

/** A video for the Gallery, made by the account's hub; the hub says when. */
export const startVideo = (request: VideoRequest) => send<VideoJob>('POST', '/gallery/videos', request);
export const listVideoJobs = () => get<VideoJobList>('/gallery/videos');

/** The original an image belongs to, then its revisions, oldest first. */
export const galleryThread = (id: string) => get<GalleryThread>(`/gallery/${encodeURIComponent(id)}/thread`);

/**
 * Make a batch, calling `onEvent` as it goes. Rejects when the batch is
 * refused before it starts, as `streamTurn` does; abort to stop what has not
 * started.
 */
export async function streamGallery(
  request: GalleryRequest,
  references: File[],
  onEvent: (e: GalleryEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const form = new FormData();
  form.set('request', JSON.stringify(request));
  for (const f of references) form.append('reference', f);
  let res: Response;
  try {
    res = await fetch(`${BASE}/gallery`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'text/event-stream' },
      body: form,
      ...(signal ? { signal } : {})
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    throw new ApiError(0, 'network', 'Could not reach Assist.');
  }
  if (!res.ok || !res.body) throw await errorFrom(res);
  await readSse(res as Response & { body: NonNullable<Response['body']> }, onEvent);
}

export const chatTurnPath = (chatId: string) =>
  `/chats/${encodeURIComponent(chatId)}/turn` as const;
export const chatRetryPath = (chatId: string) =>
  `/chats/${encodeURIComponent(chatId)}/retry` as const;
export const chatContinuePath = (chatId: string, messageId: string) =>
  `/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/continue` as const;
